import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface NewsTopic {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  created_at: string;
}

export function useNewsTopics() {
  const [topics, setTopics] = useState<NewsTopic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopics();

    const channel = supabase
      .channel('news-topics-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'news_topics' },
        fetchTopics,
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  async function fetchTopics() {
    const { data } = await supabase
      .from('news_topics')
      .select('*')
      .order('sort_order');
    setTopics(data ?? []);
    setLoading(false);
  }

  async function createTopic(name: string, slug: string) {
    const existing = topics.find((t) => t.slug === slug);
    if (existing) throw new Error(`Er bestaat al een topic met de naam '${existing.name}'.`);
    const maxOrder = topics.reduce((max, t) => Math.max(max, t.sort_order), -1);
    const { error } = await supabase
      .from('news_topics')
      .insert({ name, slug, sort_order: maxOrder + 1 });
    if (error) {
      if (error.message?.includes('duplicate key')) {
        throw new Error(`Er bestaat al een topic met deze slug '${slug}'.`);
      }
      throw error;
    }
  }

  async function updateTopic(id: string, name: string, slug: string) {
    const { error } = await supabase
      .from('news_topics')
      .update({ name, slug })
      .eq('id', id);
    if (error) throw error;
  }

  async function deleteTopic(id: string) {
    const { error } = await supabase
      .from('news_topics')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async function getTopicsForNewsItem(newsItemId: string): Promise<string[]> {
    const { data } = await supabase
      .from('news_content_topics')
      .select('topic_id')
      .eq('news_item_id', newsItemId);
    return (data ?? []).map((r) => r.topic_id);
  }

  async function setTopicsForNewsItem(newsItemId: string, topicIds: string[]) {
    await supabase
      .from('news_content_topics')
      .delete()
      .eq('news_item_id', newsItemId);
    if (topicIds.length > 0) {
      const { error } = await supabase
        .from('news_content_topics')
        .insert(topicIds.map((tid) => ({ news_item_id: newsItemId, topic_id: tid })));
      if (error) throw error;
    }
  }

  return {
    topics,
    loading,
    createTopic,
    updateTopic,
    deleteTopic,
    getTopicsForNewsItem,
    setTopicsForNewsItem,
  };
}
